import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Moon, Sun, Menu, X, Hexagon } from "lucide-react";
import { useTheme } from "../context/ThemeProvider";
import { NAV, COMPANY } from "../data/content";
import { Button } from "./ui/button";

const Header = () => {
  const { theme, toggle } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="fixed inset-x-0 top-0 z-50"
      data-testid="site-header"
    >
      <div
        className={`mx-auto mt-3 flex max-w-[1400px] items-center justify-between gap-4 px-4 transition-all duration-500 sm:px-8 ${
          scrolled ? "" : ""
        }`}
      >
        <div
          className={`flex w-full items-center justify-between rounded-2xl px-4 py-3 transition-all duration-500 sm:px-6 ${
            scrolled ? "glass shadow-lg shadow-black/5" : "bg-transparent"
          }`}
        >
          {/* Logo */}
          <a href="#top" className="flex items-center gap-2.5" data-testid="logo-link">
            <span className="relative flex h-9 w-9 items-center justify-center">
              <Hexagon className="h-9 w-9 text-navy dark:text-teal" strokeWidth={1.5} />
              <span className="absolute h-2.5 w-2.5 rounded-full bg-teal" />
            </span>
            <span className="font-display text-lg font-bold tracking-tight text-foreground">
              TechSpace<span className="text-teal">.</span>
            </span>
          </a>

          {/* Desktop nav */}
          <nav className="hidden items-center gap-1 lg:flex">
            {NAV.map((item) => (
              <a
                key={item.href}
                href={item.href}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                className="rounded-full px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={toggle}
              data-testid="theme-toggle"
              aria-label="Toggle theme"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-border text-foreground transition-colors hover:bg-secondary"
            >
              {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
            <a href="#contact" className="hidden sm:block">
              <Button
                data-testid="header-book-demo"
                className="rounded-full bg-navy px-5 font-semibold text-white hover:bg-navy/90"
              >
                Book a Demo
              </Button>
            </a>
            <button
              onClick={() => setOpen((o) => !o)}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-border lg:hidden"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="mx-4 mt-2 rounded-2xl glass p-3 shadow-xl lg:hidden"
            data-testid="mobile-menu"
          >
            {NAV.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setOpen(false)}
                className="block rounded-xl px-4 py-3 text-sm font-medium text-foreground transition-colors hover:bg-secondary"
              >
                {item.label}
              </a>
            ))}
            <a href="#contact" onClick={() => setOpen(false)}>
              <Button className="mt-2 w-full rounded-xl bg-navy font-semibold text-white">
                Book a Demo
              </Button>
            </a>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
